import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import '../../styles/datatable.scss';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getPaginationRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
} from '@tanstack/react-table';
import Header from './Header';
import Paginator from './Paginator';

const TabularDataBasic = ({
  data,
  columns,
  tableHeight,
  tableWidth,
  maxHeight,
  getCellClass,
  getGroupBorderStyle,
  initialPageSize,
  customFooter,
  autoResetPageIndex,
  onPageChangeCallback,
  ...rest
}) => {
  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: initialPageSize,
      },
    },
    state: {
      sorting
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    enableColumnResizing: false,
    enablePinning: false,
    autoResetPageIndex,
    ...rest,
  });

  useEffect(() => {
    table.setPageIndex(0);
  }, [table, data.length]);

  const bodyStyle = {};
  const tableStyle = {};
  if (maxHeight) {
    bodyStyle.maxHeight = maxHeight;
  }
  if (tableHeight) {
    bodyStyle.height = tableHeight;
  }
  if (tableWidth) {
    tableStyle.width = tableWidth;
  }

  return (
    <div data-component={'TabularDataBasic'}>
      <div className={'table-container'} style={{width: '100%'}}>
        <table
          className={'table table-striped'}
          style={tableStyle}
        >
          <thead style={{position: 'relative'}}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return <Header key={header.id} properties={header} colClassName={header.column.columnDef.meta?.className}/>;
                })}
              </tr>
            ))}
          </thead>
          <tbody style={bodyStyle}>
            {table.getRowModel().rows.map((row) => {
              const cellClass = getCellClass ? getCellClass(row) : '';
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const metaClass = cell.column.columnDef.meta?.className;
                    const borderStyle = getGroupBorderStyle ? getGroupBorderStyle(cell) : '';
                    return (
                      <td
                        key={cell.id}
                        style={{
                          display: cell.getContext().column.getSize() == 0 ? 'none' : '',
                          textAlign: cell.column.columnDef.textAlign ? cell.column.columnDef.textAlign : '',
                          ...borderStyle,
                          width: 'auto'
                        }}
                        className={classNames({[cellClass]: Boolean(cellClass), [metaClass]: Boolean(metaClass)})}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {customFooter}
        </table>
      </div>
      <Paginator
        setPageIndex={table.setPageIndex}
        getCanPreviousPage={table.getCanPreviousPage}
        getCanNextPage={table.getCanNextPage}
        getPageCount={table.getPageCount}
        currentPageIndex={table.getState().pagination.pageIndex}
        onPageChangeCallback={onPageChangeCallback}
      />
    </div>
  );
}

TabularDataBasic.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  tableHeight: PropTypes.string,
  tableWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  getCellClass: PropTypes.func,
  getGroupBorderStyle: PropTypes.func,
  initialPageSize: PropTypes.number,
  autoResetPageIndex: PropTypes.bool,
  customFooter: PropTypes.element,
  onPageChangeCallback: PropTypes.func
};

TabularDataBasic.defaultProps = {
  tableHeight: '100%',
  tableWidth: '100%',
  maxHeight: '100%',
  autoResetPageIndex: false,
  initialPageSize: 50,
  customFooter: <tfoot></tfoot>
};

export default TabularDataBasic;
